import { Box, Button, Grid, Typography, useTheme } from "@mui/material";
import { FailureIcon, InProgressIcon, SuccessTickIcon } from "../../../assets/SvgIcons";
import dayjs from "dayjs";
import { NotificationObj } from "../../../redux-store/types/investorProfile";
import { navigationData } from "../types";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import { toINR } from "../../../utils/utilityFunctions";

const notificationIconsMap: Record<string, JSX.Element> = {
    "01": <SuccessTickIcon />,
    "02": <InProgressIcon width="20" height="30" />,
    "03": <FailureIcon width="20" height="30" />,
    "04": <SuccessTickIcon />
}

const chipColorMap: Record<string, Record<string, string>> = {
    transactionType: {
        bgColor: 'success.100',
        color: 'success.800'
    },
    schemeName: {
        bgColor: 'grey.100',
        color: 'grey.800'
    },
    amount: {
        bgColor: 'warning.100',
        color: 'warning.800'
    },
    units: {
        bgColor: 'warning.100',
        color: 'warning.800'
    }
}

function CardChip({
    label,
    chipType
}: {
    label: string;
    chipType: string
}): JSX.Element {
    return (
        <Box
            sx={{
                borderRadius: "4px",
                padding: "3px 5px",
                backgroundColor: chipColorMap[chipType].bgColor
            }}
        >
            <Typography
                variant="overline_400"
                sx={{ color: chipColorMap[chipType].color, wordBreak: "break-all" }}
            >
                {label}
            </Typography>
        </Box>
    )
}

export default function Card({
    subtitle,
    timestamp,
    title,
    type,
    url,
    transactionType,
    amount,
    units,
    schemeName
}: NotificationObj): JSX.Element {
    const theme = useTheme()
    const history = useHistory();

    const { pathname: pathName, state } = history.location
    const { clientCode = '' } = state as navigationData

    // Data from redux store
    const { portfolioList = [] } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { locale = "", currencyCode = "" } = useSelector((store: RootStateType) => store.currencyInfo);

    const handleMfcNotificationClick = () => {
        const heldAwayPortfolioObj: any = (portfolioList ?? []).find((obj: any) => obj.isPortfolioHeldaway)

        if (heldAwayPortfolioObj) {
            const modifiedPathname = pathName
                .split("/")
                .filter((item) => (item !== 'notifications'))
                .join("/")

            history.push(`${modifiedPathname}/mutual-fund?tab=Overview&portfolio=${heldAwayPortfolioObj?.portfolioId ?? ""}`, {
                clientCode
            });
        }
    }

    const isMandateUrl = `${url ?? ''}`?.startsWith("https://")

    const handleMandateAuthenticationClick = () => {
        window.open(`${url}`, '_blank')
    }

    const modifiedTimestamp = dayjs(Number(timestamp ?? ""))
    const notificationDate = modifiedTimestamp.format('DD MMM, YY')
    const notificationTime = modifiedTimestamp.format('hh:mm A')

    return (
        <Grid
            container
            rowSpacing={1}
            sx={{
                padding: { xs: "15px", md: "25px" },
                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`
            }}
        >
            <Grid item xs={12} md={9}>
                <Box
                    sx={{
                        display: "flex",
                        columnGap: "10px"
                    }}
                >
                    <Box sx={{ flexShrink: 0 }}>
                        {notificationIconsMap[type]}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            rowGap: "10px"
                        }}
                    >
                        <Typography
                            variant="body_600"
                            sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 400
                            }}
                        >
                            {title}
                        </Typography>
                        {(
                            `${transactionType ?? ""}` ||
                            `${schemeName ?? ""}` ||
                            `${amount ?? ""}` ||
                            `${units ?? ""}`
                        ) && (
                                <Grid container columnSpacing={1} flexWrap="nowrap">
                                    {`${transactionType ?? ""}` && (
                                        <Grid item sx={{ flexShrink: 0 }}>
                                            <CardChip
                                                label={transactionType}
                                                chipType="transactionType"
                                            />
                                        </Grid>
                                    )}
                                    {`${schemeName ?? ""}` && (
                                        <Grid item sx={{ maxWidth: "500px" }}>
                                            <CardChip
                                                label={schemeName}
                                                chipType="schemeName"
                                            />
                                        </Grid>
                                    )}
                                    {`${amount ?? ""}` && (
                                        <Grid item sx={{ flexShrink: 0 }}>
                                            <CardChip
                                                label={`${toINR({ value: amount ?? 0, locale, currencyCode })}`}
                                                chipType="amount"
                                            />
                                        </Grid>
                                    )}
                                    {`${units ?? ""}` && (
                                        <Grid item sx={{ flexShrink: 0 }}>
                                            <CardChip
                                                label={`${units} units`}
                                                chipType="units"
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        <Typography
                            variant="body_500"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 400
                            }}
                        >
                            {subtitle}
                            {(type === '04') ? (
                                <>
                                    {" "}
                                    <Typography
                                        component="span"
                                        variant="body_500"
                                        sx={{
                                            color: theme.palette.primary[500],
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            fontWeight: 400
                                        }}
                                        onClick={handleMfcNotificationClick}
                                    >
                                        Please view
                                    </Typography>
                                </>
                            ) : null}
                            {url ? (
                                <>
                                    {" "}
                                    <Button
                                        variant="text"
                                        disabled={!isMandateUrl}
                                        sx={{
                                            color: theme.palette.primary[500],
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                              padding:0,
                                              minWidth:'auto',
                                             '&.Mui-disabled': {
                                                 color: theme.palette.text.secondary,
                                                textDecoration: 'none'
                                             }
                                        }}
                                        onClick={handleMandateAuthenticationClick}
                                    >
                                        Link
                                    </Button>
                                </>
                            ) : null}
                        </Typography>
                        <Typography
                            variant="body_500"
                            sx={{
                                color: theme.palette.text.secondary,
                                display: { xs: "block", md: "none" }
                            }}
                        >
                            {notificationDate} {notificationTime}
                        </Typography>
                    </Box>

                </Box>
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: { xs: "none", md: "block" } }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                        alignItems: "center",
                        height: "100%"
                    }}
                >
                    <Typography
                        variant="body_500"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        {notificationDate} {notificationTime}
                    </Typography>
                </Box>
            </Grid>
        </Grid >
    )
}